/**
 * Quality / <a check>: the findings of one check of the last run (lint, secrets, security, vulnerabilities,
 * dependencies, SBOM), filtered by severity, service and words, the ignored ones on demand. Each finding: severity,
 * message and rule, where (file:line, a link to the code), service, the fix, an "Ignored" badge with its reason, and for
 * editors "Ignore…", which opens the ignore form prefilled (the finding's rule or advisory id, the check, its file). A
 * finding of a service whose Deploy is off (Apps) says "Not deployed: doesn't block Publish" (a secret, or a licence not
 * allowed: "Not deployed: still blocks Git", since Git and GitOps check those in every service).
 * Fix with AI hands the findings shown to the project chat.
 * Dependencies also lists which lockfile each service has (or none, with Create lockfile) and every package with its
 * licence and what is wrong with it (QualityPackages); a licence chip filters that list. A check that found nothing
 * says what that means with its counts ("No licence problems: all 197 packages use an allowed licence."), never a bare
 * "Nothing found." (qualityModel passingMessage).
 * Layout (one coherent state): the head on one row (← Quality, the check, its status, the last run and tools, Run on
 * the right); then the summary; then either one calm empty state (Dependencies, Vulnerabilities, SBOM where no service
 * declares dependencies: nothing to look up, no lockfile banner) or the area details, the filters toolbar (only when
 * there is something to filter) and the findings.
 * Run on this page reports why the checks did not start right under the head (`runNote`), and a check the sandbox
 * restart stopped says so there with Retry ("Interrupted — the sandbox restarted").
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Play, Sparkle } from '@phosphor-icons/react';
import { addIgnore, downloadSbom, getCheck } from '../../services/QualityService';
import { moveFindingToSecret } from '../../services/AppConfigService';
import { Banner, IgnoreForm, Pill, State, StatusPill, SubHead } from './QualityCommon';
import AreaDetails from './QualityAreaDetails';
import { NOT_DEPLOYED_TIP } from '../../utils/deployFlags';
import DataTable from '../table/DataTable';
import { LockfilesByService, PackagesTable } from './QualityPackages';
import {
  SEVERITIES, accessibilityPrompt, checkTitle, errorText, fileLine, findingsPrompt, formatDuration, interruptedCheck, passingMessage, plural, readinessItemOf, readinessPrompt, relativeTime, severityOf, vulnerabilityPrompt,
} from './qualityModel';

const packageOf = (f) => (f.extra?.package ? `${f.extra.package}${f.extra.version ? ` ${f.extra.version}` : ''}` : null);

const OUTDATED_COLUMNS = [
  { key: 'package', label: 'Package', value: (o) => o.package, className: 'quality-mono' },
  { key: 'service', label: 'Service', value: (o) => o.service, width: 160 },
  { key: 'current', label: 'Current', value: (o) => o.current, width: 120 },
  { key: 'latest', label: 'Latest', value: (o) => o.latest, width: 150, title: (o) => o.latest, render: (o) => <>{o.latest}{o.major ? <Pill className="is-xs" tone="warn">major</Pill> : null}</> },
];

/** The findings' columns: severity, the finding (up to three lines, the whole message on hover), where, service, fix. */
function findingColumns(onOpenCode, checkId) {
  return [
    { key: 'severity', label: 'Severity', width: 100, value: (f) => SEVERITIES.indexOf(f.severity), render: (f) => { const sev = severityOf(f.severity); return <Pill tone={sev.tone}>{sev.label}</Pill>; } },
    {
      key: 'finding',
      label: 'Finding',
      minWidth: 220,
      wrap: 3,
      value: (f) => f.message,
      title: (f) => [packageOf(f), f.message, f.rule].filter(Boolean).join(' · '),
      render: (f) => (
        <>
          {packageOf(f) && <span className="quality-mono">{packageOf(f)} · </span>}
          {f.message}
          <span className="quality-muted quality-mono quality-small"> {f.rule}</span>
          {f.ignored && <> <Pill className="is-xs" title={`${f.ignored.reason} (${f.ignored.by}${f.ignored.until ? `, until ${f.ignored.until}` : ''})`} tone="info">Ignored: {f.ignored.reason}</Pill></>}
        </>
      ),
    },
    {
      key: 'where',
      label: 'Where',
      width: 170,
      className: 'quality-mono quality-small',
      value: (f) => fileLine(f) || f.extra?.lockfile || '',
      title: (f) => fileLine(f) || f.extra?.lockfile || '',
      render: (f) => {
        const where = fileLine(f);
        return where && onOpenCode ? <button className="quality-link quality-mono" onClick={() => onOpenCode({ path: f.file, line: f.line })} type="button">{where}</button> : where || f.extra?.lockfile || '–';
      },
    },
    {
      key: 'service',
      label: 'Service',
      width: 130,
      wrap: 2,
      value: (f) => f.service,
      title: (f) => f.service,
      render: (f) => <>{f.service || '–'}{f.notDeployed && <> <Pill className="is-xs" title={NOT_DEPLOYED_TIP} tone="info">{checkId === 'secrets' || (checkId === 'dependencies' && f.rule === 'licence-not-allowed') ? 'Not deployed: still blocks Git' : <>Not deployed: doesn&apos;t block Publish</>}</Pill></>}</>,
    },
    { key: 'fix', label: 'Fix', width: 180, wrap: 2, className: 'quality-small', value: (f) => f.fix, title: (f) => f.fix, render: (f) => f.fix || '–' },
  ];
}

const FIXABLE = ['vulnerabilities', 'security', 'secrets', 'lint', 'dependencies', 'readiness', 'privacy', 'accessibility', 'performance', 'codeScanning', 'external', 'lighthouse'];

/**
 * Secrets › "Move to secrets": the value becomes a secret of its service (Configuration; never shown again) and the
 * literal in the code reads it from the environment — changed directly when Flow can (JS/TS, Python), or by the AI.
 */
function MoveToSecretForm({ projectId, finding, onAskAI, onCancel, onDone }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const move = async (mode) => {
    setBusy(true);
    setError(null);
    try {
      const result = await moveFindingToSecret(projectId, { findingId: finding.id, name: name.trim() || undefined, mode });
      if (result.prompt && onAskAI) { onAskAI(result.prompt); }
      onDone(result);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="quality-move-secret" data-testid="quality-move-secret">
      <p className="quality-muted quality-small">The value is saved as a secret of <strong>{finding.service}</strong> (encrypted, never in git, never shown again) and the code reads it from <code>process.env</code>. It stays in the project history: rotate it afterwards.</p>
      <div className="quality-row">
        <input aria-label="Secret name" className="quality-input quality-mono" onChange={(e) => setName(e.target.value.toUpperCase())} placeholder="Name (suggested if empty)" value={name} />
        <button className="quality-btn is-sm is-primary" disabled={busy} onClick={() => move('patch')} type="button">Move and change the code</button>
        {onAskAI && <button className="quality-btn is-sm" disabled={busy} onClick={() => move('prompt')} type="button">Move, AI changes the code</button>}
        <button className="quality-btn is-sm is-quiet" onClick={onCancel} type="button">Cancel</button>
      </div>
      {error && <p className="quality-ink-fail quality-small" role="alert">{error}</p>}
    </div>
  );
}

/** The checks that read the services' lockfiles (an empty package list is "nothing to check", never "passing"). */
const DEPENDENCY_CHECKS = ['dependencies', 'vulnerabilities', 'sbom'];

/**
 * True when a dependency check has nothing to look at because no service declares dependencies: no package, no
 * lockfile, and no service missing one (that case is the "No lockfile" banner instead).
 */
export function noDependencies(checkId, check) {
  if (!DEPENDENCY_CHECKS.includes(checkId) || !check) { return false; }
  const d = check.details || {};
  if (d.packages || d.components) { return false; }
  if ((d.lockfiles || []).length || (d.noLockfile || []).length) { return false; }
  if ((check.findings || []).some((f) => f.rule === 'no-lockfile') || check.findingsTotal || check.ignoredCount) { return false; }
  return check.status !== 'offline';
}

/** "OSV api.osv.dev v1 · just now, took 1 ms": the tools and the last run, for the head's row. */
function runMeta(check) {
  // The dependency check's own reader is the tool "lockfiles" version 1: "lockfiles 1" read as a count of lockfiles.
  const tools = (check?.tools || []).map((t) => (t.name === 'lockfiles' ? 'lockfile reader' : `${t.name}${t.version ? ` ${t.version}` : ''}`)).join(', ');
  const ran = check?.startedAt ? `${relativeTime(check.startedAt)}, took ${formatDuration(check.durationMs)}` : '';
  return [tools, ran].filter(Boolean).join(' · ');
}

/** What an ignore rule for this finding names: the advisory id or the rule (a package for licence findings). */
function ignoreTarget(checkId, finding) {
  if (checkId === 'dependencies' && finding.extra?.package) { return finding.extra.package; }
  return finding.rule || finding.id;
}

export default function QualityCheck({
  projectId, checkId, overview, canEdit = false, onAskAI = null, onBack, onRun, onOpenCode = null, onSaved = null,
  onRunA11y = null, onOpenCheck = null, onCreateLockfiles = null, lockfileBusy = false, lockfileStatus = null, runNote = null,
}) {
  const [check, setCheck] = useState(null);
  const [error, setError] = useState(null);
  const [severity, setSeverity] = useState('');
  // The tab's "Checks of" service (QualityScope) is where it starts.
  const [service, setService] = useState(overview?.service || '');
  const [find, setFind] = useState('');
  const [q, setQ] = useState('');
  const [showIgnored, setShowIgnored] = useState(false);
  const [ignoring, setIgnoring] = useState(null);
  const [moving, setMoving] = useState(null);
  const [moved, setMoved] = useState(null);
  // Dependencies › the licence the packages table shows ('' every one): a licence chip or the table's filter sets it.
  const [licence, setLicence] = useState('');
  const runId = overview?.run?.runId || null;
  const card = overview?.checks?.[checkId] || null;
  const running = Boolean(overview?.running);
  const services = (overview?.detected?.services || []).map((s) => s.service);

  useEffect(() => {
    const timer = setTimeout(() => setQ(find.trim()), 300);
    return () => clearTimeout(timer);
  }, [find]);

  const load = useCallback(async () => {
    try {
      setCheck(await getCheck(projectId, checkId, { severity, service, q, includeIgnored: showIgnored }));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [projectId, checkId, severity, service, q, showIgnored]);

  useEffect(() => { load(); }, [load, runId]);

  const saveIgnore = async (rule) => {
    await addIgnore(projectId, rule);
    setIgnoring(null);
    onSaved?.();
    load();
  };

  const findings = check?.findings || [];
  const open = findings.filter((f) => !f.ignored);
  const title = checkTitle(checkId);
  const fixPrompt = () => {
    if (checkId === 'vulnerabilities') { return open.slice(0, 5).map(vulnerabilityPrompt).join('\n\n---\n\n'); }
    if (checkId === 'readiness') { return readinessPrompt(open.map(readinessItemOf)); }
    if (checkId === 'accessibility') { return accessibilityPrompt(open); }
    return findingsPrompt(checkId, open);
  };
  const runThis = () => (checkId === 'accessibility' && onRunA11y ? onRunA11y() : onRun({ checks: [checkId] }));
  // The run that was to do it stopped when the sandbox restarted, and it has not run since.
  const interrupted = interruptedCheck(overview, checkId) ? (
    <Banner
      actions={canEdit ? <button className="quality-btn is-sm is-primary" data-testid="quality-check-retry" onClick={runThis} type="button"><Play aria-hidden size={12} weight="fill" />Retry</button> : null}
      testId="quality-check-interrupted"
      tone="warn"
    >
      <span>Interrupted — the sandbox restarted before {title.toLowerCase()} finished.{card?.ranAt ? ' What shows here is from the run before.' : ''}</span>
    </Banner>
  ) : null;

  const meta = runMeta(check);
  const head = (
    <SubHead onBack={onBack} title={title}>
      {card && <StatusPill status={running && overview.running.checks?.[checkId] !== 'done' ? 'running' : card.status} />}
      {card?.blocks && <Pill tone="fail">Blocks Publish</Pill>}
      {meta && <span className="quality-head-meta" data-testid="quality-check-meta" title={meta}>{meta}</span>}
      <span className="quality-grow" />
      {canEdit && onAskAI && FIXABLE.includes(checkId) && open.length > 0 && (
        <button className="quality-btn" onClick={() => onAskAI(fixPrompt())} type="button"><Sparkle aria-hidden size={13} />Fix with AI</button>
      )}
      {canEdit && <button className="quality-btn is-primary" disabled={running} onClick={runThis} type="button"><Play aria-hidden size={13} weight="fill" />Run {title.toLowerCase()}</button>}
    </SubHead>
  );
  // Under the head, by its Run button: why the checks did not start, and a run the restart stopped.
  const headNotes = runNote || interrupted ? <>{runNote}{interrupted}</> : null;

  if (error && !check) {
    const notRun = error.code === 'not_run' || (error.status === 404 && error.code !== 'sandbox_outdated');
    return (
      <div className="quality-sub" data-testid="quality-check">
        {head}
        {headNotes}
        {notRun
          ? <State title={`${title} has not run yet`}>{canEdit ? 'Run it to see what it finds.' : 'An editor can run it.'}</State>
          : <State action={<button className="quality-btn" onClick={load} type="button">Try again</button>} title={`Could not load ${title.toLowerCase()}`} tone="bad">{errorText(error)}</State>}
      </div>
    );
  }
  if (!check) {
    return <div className="quality-sub" data-testid="quality-check">{head}{headNotes}<div className="quality-state" role="status">Loading…</div></div>;
  }

  const details = check.details || {};
  if (noDependencies(checkId, check)) {
    return (
      <div className="quality-sub" data-testid="quality-check">
        {head}
        {headNotes}
        <State testId="quality-no-dependencies" title="No dependencies to check">
          None of the services declares dependencies, so there is nothing to look up. Add a dependency to a service and run the check again.
        </State>
      </div>
    );
  }
  const filtered = Boolean(q || severity || showIgnored || (service && service !== (overview?.service || '')));
  const showFilters = findings.length > 0 || filtered || Boolean(check.ignoredCount);
  // The lockfile banner below already says why nothing was checked; the server's line would repeat it.
  const lockless = DEPENDENCY_CHECKS.includes(checkId) && /no lockfile/i.test(check.message || '');
  // The services with something to say (a service a check is not needed for: the summary says why, once).
  const notes = (check.services || []).filter((s) => !['pass', 'not_needed'].includes(s.status) && s.message);
  // Dependencies lists every package below (with its own "no problems" line): no second, empty findings box.
  // The SBOM lists its components the same way (2026-10-31: "525 components" over a bare "Nothing found.").
  const packagesListed = (checkId === 'dependencies' && Boolean(details.packages)) || (checkId === 'sbom' && check.status !== 'not_run');
  const passing = filtered ? null : passingMessage(checkId, check);
  return (
    <div className="quality-sub" data-testid="quality-check">
      {head}
      {headNotes}
      {check.summary && <p className="quality-muted" data-testid="quality-check-summary">{check.summary}</p>}
      {check.message && !lockless && <Banner tone={check.status === 'offline' ? 'warn' : 'info'}>{check.message}</Banner>}
      {notes.length > 0 && (
        <ul aria-label="Per service" className="quality-notes">
          {notes.map((s) => (
            <li key={s.service}><span className="quality-mono">{s.service}</span><StatusPill status={s.status} /><span>{s.message}</span></li>
          ))}
        </ul>
      )}

      <AreaDetails
        canEdit={canEdit}
        check={check}
        checkId={checkId}
        lockfileBusy={lockfileBusy}
        onAskAI={onAskAI}
        onChanged={() => { onSaved?.(); load(); }}
        onCreateLockfiles={onCreateLockfiles}
        onOpenCode={onOpenCode}
        onRunA11y={onRunA11y}
        projectId={projectId}
      />
      {/* Create lockfiles clicked here: its progress, then its result. */}
      {lockfileStatus}

      {checkId === 'dependencies' && (
        <LockfilesByService canEdit={canEdit} details={details} detected={overview?.detected?.services || []} lockfileBusy={lockfileBusy} onCreateLockfiles={onCreateLockfiles} />
      )}

      {checkId === 'dependencies' && details.licences?.length > 0 && (
        <section aria-labelledby="quality-licences-title" className="quality-card quality-pad">
          <h2 id="quality-licences-title">Licences <span className="quality-muted quality-small">· {plural(details.packages || 0, 'package')}</span></h2>
          <ul aria-label="Licences: pick one to list its packages" className="quality-tags">
            {details.licences.map((l) => (
              <li className={`${l.allowed === false ? 'is-bad' : l.allowed === true ? '' : 'is-unknown'}${licence === l.licence ? ' is-on' : ''}`} key={l.licence}>
                <button aria-pressed={licence === l.licence} className="quality-tag-btn" onClick={() => setLicence(licence === l.licence ? '' : l.licence)} title={licence === l.licence ? 'Show every licence' : `List the ${l.licence} packages`} type="button">
                  <span className="quality-mono">{l.licence}</span> {l.count}{l.allowed === false ? ' · not allowed' : l.allowed === true ? '' : ' · unknown'}
                </button>
              </li>
            ))}
          </ul>
          {details.outdated === null && details.outdatedNote && <p className="quality-muted quality-small">{details.outdatedNote}</p>}
          {details.outdated?.length > 0 && (
            <div className="quality-table-box">
              <DataTable
                className="quality-table"
                columns={OUTDATED_COLUMNS}
                rowKey={(o) => `${o.service}:${o.package}`}
                rows={details.outdated.slice(0, 100)}
                storageKey="flow.quality.outdated.hiddenColumns"
              />
            </div>
          )}
        </section>
      )}

      {packagesListed && (
        <section aria-labelledby="quality-packages-title" className="quality-card quality-pad">
          <h2 id="quality-packages-title">{checkId === 'sbom' ? 'Components' : 'Packages'}</h2>
          {checkId === 'sbom' && (
            <p className="quality-actions" data-testid="quality-sbom-downloads">
              <button className="quality-btn is-sm" onClick={() => downloadSbom(projectId, 'cyclonedx').catch(() => {})} type="button">Download CycloneDX</button>
              <button className="quality-btn is-sm" onClick={() => downloadSbom(projectId, 'spdx').catch(() => {})} type="button">Download SPDX</button>
            </p>
          )}
          <PackagesTable check={check} licence={licence} onLicence={setLicence} onOpenCheck={onOpenCheck} projectId={projectId} service={service} />
        </section>
      )}

      {showFilters && (
      <div aria-label="Filter the findings" className="quality-filters" data-testid="quality-filters" role="group">
        <label className="quality-sr" htmlFor="quality-find">Find</label>
        <input className="quality-input" id="quality-find" onChange={(e) => setFind(e.target.value)} placeholder="Rule, file, message…" type="search" value={find} />
        <label className="quality-sr" htmlFor="quality-severity">Severity</label>
        <select className="quality-input" id="quality-severity" onChange={(e) => setSeverity(e.target.value)} value={severity}>
          <option value="">Every severity</option>
          {SEVERITIES.map((s) => <option key={s} value={s}>{severityOf(s).label}</option>)}
        </select>
        {services.length > 1 && (
          <>
            <label className="quality-sr" htmlFor="quality-service">Service</label>
            <select className="quality-input" id="quality-service" onChange={(e) => setService(e.target.value)} value={service}>
              <option value="">Every service</option>
              {services.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </>
        )}
        <label className="quality-check-label"><input checked={showIgnored} onChange={(e) => setShowIgnored(e.target.checked)} type="checkbox" />Show ignored</label>
      </div>
      )}

      {moved && (
        <Banner onDismiss={() => setMoved(null)} testId="quality-moved">
          <span>Moved to the secret <strong>{moved.name}</strong> of {moved.service}{moved.patched ? `: ${moved.file} now reads process.env.${moved.name}.` : ': the AI is changing the code.'} {moved.rotate} Run the check again to confirm.</span>
        </Banner>
      )}
      {findings.length === 0 ? (
        !(packagesListed && !filtered && check.status !== 'offline') && (
          <State testId="quality-findings-empty" title={q || severity || service ? 'No finding matches.' : passing || 'Nothing found.'}>{check.status === 'offline' ? check.message : null}</State>
        )
      ) : (
        <div className="quality-card quality-table-box">
          <DataTable
            actions={{
              label: 'Actions',
              width: canEdit && checkId === 'secrets' ? 190 : 96,
              render: (f) => (
                <span className="quality-actions">
                  {canEdit && !f.ignored && checkId === 'secrets' && f.service && f.extra?.live !== false && (
                    <button className="quality-btn is-sm" onClick={() => { setMoving(moving === f.id ? null : f.id); setIgnoring(null); }} type="button">Move to secrets</button>
                  )}
                  {canEdit && !f.ignored && <button className="quality-btn is-sm is-quiet" onClick={() => { setIgnoring(ignoring === f.id ? null : f.id); setMoving(null); }} type="button">Ignore…</button>}
                </span>
              ),
            }}
            after={(f, { colCount }) => (
              <>
                {moving === f.id && (
                  <tr className="is-form">
                    <td colSpan={colCount}>
                      <MoveToSecretForm
                        finding={f}
                        onAskAI={onAskAI}
                        onCancel={() => setMoving(null)}
                        onDone={(result) => { setMoving(null); setMoved(result); onSaved?.(); load(); }}
                        projectId={projectId}
                      />
                    </td>
                  </tr>
                )}
                {ignoring === f.id && (
                  <tr className="is-form">
                    <td colSpan={colCount}>
                      <IgnoreForm
                        initial={{ what: ignoreTarget(checkId, f), check: checkId === 'sbom' ? '' : checkId, path: f.file || '' }}
                        onCancel={() => setIgnoring(null)}
                        onSave={saveIgnore}
                      />
                    </td>
                  </tr>
                )}
              </>
            )}
            className="quality-table"
            columns={findingColumns(onOpenCode, checkId)}
            rowKey="id"
            rowProps={(f) => ({ className: f.ignored ? 'is-ignored' : undefined, 'data-testid': `quality-finding-${f.id}` })}
            rows={findings}
            storageKey="flow.quality.findings.hiddenColumns"
            testId="quality-findings"
          />
          {check.truncated && <p className="quality-muted quality-small quality-pad-foot">Only the first findings are kept: fix these and run again to see the rest.</p>}
        </div>
      )}
    </div>
  );
}

QualityCheck.propTypes = {
  canEdit: PropTypes.bool,
  checkId: PropTypes.string.isRequired,
  onAskAI: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  // (services) => void: "Create lockfiles" (dependencies, vulnerabilities, SBOM without a lockfile).
  onCreateLockfiles: PropTypes.func,
  lockfileBusy: PropTypes.bool,
  // What Create lockfiles clicked on this page shows (QualityLockfiles LockfileStatus), or null.
  lockfileStatus: PropTypes.node,
  onOpenCode: PropTypes.func,
  // () => void: runs the accessibility check in this browser.
  onRunA11y: PropTypes.func,
  onRun: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  // (checkId) => void: opens another check (Dependencies › a vulnerable package opens Vulnerabilities).
  onOpenCheck: PropTypes.func,
  overview: PropTypes.object,
  projectId: PropTypes.string.isRequired,
  // Why the checks did not start when Run was pressed here (QualityPanel RunNote), or null.
  runNote: PropTypes.node,
};
